import {
  Avatar,
  Flex,
  type TableColumnsType,
  Tag,
  Tooltip,
  Typography
} from "antd"
import type { BaseOptionType } from "antd/lib/select"
import React from "react"
import { Link } from "react-router-dom"

import { RecordRef } from "../../../../../components/RecordRef.tsx"
import { CreatedCell } from "../../../../../components/Table/CreatedCell.tsx"
import { empty } from "../../../../../constants/placeholders"
import { RoutesPaths } from "../../../../../constants/routes.ts"
import { budgetFormat } from "../../../../../utils/number/budget-format.ts"
import { getEntityName } from "../../../../../utils/user/get-entity-name"
import { LEAD_STATUSES } from "../../../../Lead/constants/lead-statuses.ts"
import type { LeadSalesManager } from "../../../../Lead/types/lead.types.ts"
import { LeadAppointmentCell } from "../../../components/LeadAppointmentCell.tsx"
import { LeadFollowUpCell } from "../../../components/LeadFollowUpCell.tsx"
import { LeadOperationalStatesCell } from "../../../components/LeadOperationalStatesCell.tsx"
import { LeadsStatusCell } from "../../../components/LeadsStatusCell.tsx"
import { leadSourceIcon } from "../../../constants/lead-source-icons.tsx"
import {
  abbreviateName,
  shortRelativeTime
} from "../../../helpers/lead-table-format.ts"
import { type LeadListItem } from "../../../types/types.ts"
import { getLeadsColumnActions } from "./column-actions"

const { Text } = Typography

// Shared with LeadsListEditableRow, which matches its editors to these columns
// by key.
export const LEADS_COLUMN_KEYS = {
  uid: "uid",
  customer: "customer",
  leadName: "lead_name",
  status: "status",
  operationalStates: "operational_states",
  salesManager: "sales_manager",
  followUp: "follow_up",
  appointment: "appointment",
  source: "source",
  salesPrice: "sales_price",
  lastActivity: "updated",
  dateCreated: "created",
  engagedDate: "engaged_on"
} as const

type Translate = (key: string, options?: Record<string, unknown>) => string

interface GetColumnsArg {
  t: Translate
  statuses: BaseOptionType[]
  sources: BaseOptionType[]
  archiveLabel: string
  editAllowed?: boolean
  isMobile?: boolean
  onSelectRow: (record: LeadListItem, action: string, payload?: unknown) => void
}

const locationOf = (record: LeadListItem) =>
  [record?.address, record?.city, record?.state, record?.zip_code]
    .filter(Boolean)
    .join(", ")

export const getColumns = ({
  t,
  onSelectRow,
  statuses,
  sources,
  archiveLabel,
  editAllowed,
  isMobile
}: GetColumnsArg): TableColumnsType<LeadListItem> => {
  const statusMap = statuses.reduce((acc, { label, value }) => {
    acc[label] = value
    return acc
  }, {})

  // The list serializer sends source as its display label; the icon map is
  // keyed on the enum value, so the options are what bridge the two.
  const sourceValueByLabel = new Map(
    (sources || []).map(({ label, value }) => [label, String(value)])
  )

  return [
    {
      title: t("leadsTable.columns.leadId"),
      dataIndex: "uid",
      key: LEADS_COLUMN_KEYS.uid,
      width: 80,
      render: (uid: string | null) => <RecordRef uid={uid} compact short />
    },
    {
      title: t("leadsTable.columns.customer"),
      sorter: true,
      dataIndex: "customer__first_name",
      key: LEADS_COLUMN_KEYS.customer,
      showSorterTooltip: {
        title: t("leadsTable.columns.sortByCustomer")
      },
      width: 116,
      render: (_, record) => {
        const entity = record?.customer || record?.temporary_customer
        const fullName = getEntityName(entity)
        const contact = [entity?.email, entity?.phone]
          .filter(Boolean)
          .join(" · ")

        // One line, as the design has it — three lines here set the height of
        // every row in the table. The contact details stay reachable through
        // the row's own call and message actions, and on hover.
        return (
          <Text strong ellipsis={{ tooltip: contact || fullName }}>
            {fullName}
          </Text>
        )
      }
    },
    {
      title: t("leadsTable.columns.project"),
      dataIndex: "lead_name",
      key: LEADS_COLUMN_KEYS.leadName,
      sorter: true,
      width: 136,
      render: (value: string, record) => {
        const location = locationOf(record)

        return (
          <Flex vertical>
            <Link to={RoutesPaths.lead.view(record?.id)}>
              <Text className={"text-inherit"} ellipsis={{ tooltip: value }}>
                {value}
              </Text>
            </Link>
            <Text type={"secondary"} ellipsis={{ tooltip: location }}>
              {location || empty}
            </Text>
          </Flex>
        )
      }
    },
    {
      title: t("leadsTable.columns.status"),
      sorter: true,
      dataIndex: "status",
      key: LEADS_COLUMN_KEYS.status,
      width: 110,
      render: (status: string, record: LeadListItem) => (
        <LeadsStatusCell
          statusDisplay={status}
          statusMap={statusMap}
          statuses={statuses}
          isProject={record?.is_project || false}
          editable={editAllowed}
          onSelect={(targetStatus) =>
            onSelectRow(record, "change_status", { targetStatus })
          }
        />
      )
    },
    {
      title: t("leadsTable.columns.operationalState"),
      dataIndex: "operational_states",
      key: LEADS_COLUMN_KEYS.operationalStates,
      // A lead carries several states at once, so the server ranks it by the
      // most significant one it holds rather than comparing the raw array.
      sorter: true,
      showSorterTooltip: {
        title: t("leadsTable.columns.sortByOperationalState")
      },
      width: 96,
      render: (_, record) => (
        <LeadOperationalStatesCell
          states={record?.operational_states}
          followUpDate={record?.follow_up_date}
          editable={editAllowed && !record?.is_project}
          onChange={(operationalStates) =>
            onSelectRow(record, "change_operational_states", operationalStates)
          }
        />
      )
    },
    {
      title: t("leadsTable.columns.owner"),
      dataIndex: "sales_manager",
      key: LEADS_COLUMN_KEYS.salesManager,
      width: 140,
      render: (sales_manager: LeadSalesManager[]) => {
        if (!sales_manager?.length) return empty

        // The primary assignment is the owner the lifecycle asks for; the rest
        // are collapsed behind a count. Listing them all made a shared lead four
        // lines tall and set the height of every row in the table.
        const [primary, ...rest] = [...sales_manager].sort(
          (a, b) => Number(b?.is_main) - Number(a?.is_main)
        )
        const fullName = getEntityName(primary)
        const shortName =
          abbreviateName(primary?.first_name, primary?.last_name) || fullName

        return (
          <Flex gap={8} align={"center"} className={"cursor-pointer"}>
            <Avatar className={"shrink-0"} src={primary?.avatar} size="small">
              {primary?.first_name?.charAt(0)}
            </Avatar>
            <Text
              strong={primary?.is_main}
              className={"min-w-0"}
              ellipsis={{ tooltip: fullName }}
            >
              {shortName}
            </Text>
            {!!rest.length && (
              <Tooltip title={rest.map((s) => getEntityName(s)).join(", ")}>
                <Tag className={"m-0 shrink-0"}>{`+${rest.length}`}</Tag>
              </Tooltip>
            )}
          </Flex>
        )
      }
    },
    {
      title: t("leadsTable.columns.followUp"),
      dataIndex: "follow_up_date",
      key: LEADS_COLUMN_KEYS.followUp,
      sorter: true,
      width: 136,
      render: (_, record) => (
        <LeadFollowUpCell
          date={record?.follow_up_date}
          time={record?.follow_up_time}
          nurture={record?.current_status === LEAD_STATUSES.NURTURING}
          eventId={record?.follow_up_event}
          onEdit={
            editAllowed ? () => onSelectRow(record, "edit_follow_up") : undefined
          }
          onCompleted={() => onSelectRow(record, "refresh_row")}
        />
      )
    },
    {
      title: t("leadsTable.columns.appointment"),
      dataIndex: "appointment_date",
      key: LEADS_COLUMN_KEYS.appointment,
      sorter: true,
      width: 136,
      render: (_, record) => (
        <LeadAppointmentCell
          date={record?.appointment_date}
          time={record?.appointment_time}
          eventId={record?.appointment_event}
          onEdit={
            editAllowed ? () => onSelectRow(record, "edit_appointment") : undefined
          }
        />
      )
    },
    {
      title: t("leadsTable.columns.source"),
      sorter: true,
      dataIndex: "source",
      key: LEADS_COLUMN_KEYS.source,
      width: 72,
      render: (label: string) => {
        if (!label) return empty

        // The mark carries the meaning in the design, so the label moves to a
        // tooltip — and stays the accessible name, since an icon alone is not
        // readable by a screen reader.
        return (
          <Tooltip title={label}>
            <span
              aria-label={label}
              className={"inline-flex text-slate-500"}
              style={{ fontSize: 18, lineHeight: 0 }}
            >
              {leadSourceIcon(sourceValueByLabel.get(label))}
            </span>
          </Tooltip>
        )
      }
    },
    {
      title: t("leadsTable.columns.value"),
      dataIndex: "sales_price",
      key: LEADS_COLUMN_KEYS.salesPrice,
      sorter: true,
      width: 112,
      render: (sales_price: string) => {
        const formatted = budgetFormat(sales_price)
        return (
          <Text className={"cursor-pointer"} ellipsis={{ tooltip: formatted }}>
            {formatted}
          </Text>
        )
      }
    },
    {
      title: t("leadsTable.columns.lastActivity"),
      sorter: true,
      dataIndex: "updated",
      key: LEADS_COLUMN_KEYS.lastActivity,
      width: 84,
      render: (value: string) => {
        const relative = shortRelativeTime(value)
        if (!relative) return empty
        // The exact timestamp stays reachable on hover.
        return <Text ellipsis={{ tooltip: value }}>{relative}</Text>
      }
    },

    {
      title: t("leadsTable.columns.dateCreated"),
      sorter: true,
      // Sorts by the received date when set; the key stays `created` so saved
      // column preferences carry over.
      dataIndex: "created_on",
      key: LEADS_COLUMN_KEYS.dateCreated,
      width: 112,
      render: (_: unknown, record) => (
        <CreatedCell value={record.created} day={record.created_on} />
      )
    },

    {
      title: t("leadsTable.columns.engagedDate"),
      sorter: true,
      dataIndex: "engaged_on",
      key: LEADS_COLUMN_KEYS.engagedDate,
      width: 112,
      render: (value: string | null) => <CreatedCell value={value} />
    },

    getLeadsColumnActions({
      t,
      onSelectRow,
      editAllowed,
      archiveLabel,
      isMobile
    })
  ]
}
